import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box, Typography, Card, Grid, Slider, Chip, Stack, Button, Tabs, Tab,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, Autocomplete, TextField, Skeleton, Alert, Divider,
} from '@mui/material';
import {
  Speed, Science, LibraryAdd, Delete, RestartAlt, Leaderboard as LeaderboardIcon,
  PersonSearch, AddCircleOutline,
} from '@mui/icons-material';
import { useTheme, alpha } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { PitchExplorer } from './Explorer';
import { useYear } from '../context/YearContext';
import useMobile from '../hooks/useMobile';
import PageYearSelector from '../components/common/PageYearSelector';
import { pitchSplitsApi } from '../services/apiService';
import { PITCH_NAMES, PITCH_COLORS } from '../utils/pitchTypes';
import { playerProfileUrl } from '../utils/playerSlug';

// ============================================================================
// PITCH LAB — Designer / Arsenal Builder / Stuff+ Leaderboard
// Client-side interactive scoring against the real league corpus:
//   - Stuff+ formula is an exact replica of scripts/pullPlayByPlaySplits.cjs
//     (weights + baselines are served by /v2/pitch-splits/lab/corpus)
//   - Whiff% is predicted via kNN over real pitcher-pitchtype rows
// ============================================================================

const DESIGNER_TYPES = ['FF', 'SI', 'FC', 'SL', 'ST', 'CU', 'KC', 'CH', 'FS'];

const gradeFor = (stuff) => {
  if (stuff >= 130) return { label: 'Elite', color: '#d32f2f' };
  if (stuff >= 120) return { label: 'Plus-Plus', color: '#f57c00' };
  if (stuff >= 110) return { label: 'Plus', color: '#fbc02d' };
  if (stuff >= 95) return { label: 'Average', color: '#7cb342' };
  if (stuff >= 85) return { label: 'Below Avg', color: '#0288d1' };
  return { label: 'Fringe', color: '#78909c' };
};

// Exact replica of scoreComponent in pullPlayByPlaySplits.cjs
const scoreComponent = (actual, benchmark, std) => {
  if (!std) return 100;
  return 100 + ((actual - benchmark) / std) * 20;
};

/**
 * Score a designed pitch with the production Stuff+ formula.
 * @returns {number|null} clamped [40, 200]
 */
function stuffScore(design, baseline, weights, predictedWhiff) {
  if (!baseline || !weights) return null;
  const veloScore = scoreComponent(design.velo, baseline.velo, baseline.stdVelo);
  let ivbScore;
  if (weights.ivbDir === 1) {
    ivbScore = scoreComponent(design.ivb, baseline.ivb, baseline.stdIvb);
  } else if (weights.ivbDir === -1) {
    ivbScore = scoreComponent(-design.ivb, -baseline.ivb, baseline.stdIvb);
  } else {
    ivbScore = scoreComponent(Math.abs(design.ivb), Math.abs(baseline.ivb), baseline.stdIvb);
  }
  const hbScore = scoreComponent(Math.abs(design.hb), baseline.hb, baseline.stdHb);
  const whiffScore = scoreComponent(predictedWhiff, baseline.whiff ?? 25, baseline.stdWhiff || 8);
  const composite =
    veloScore * weights.veloW + ivbScore * weights.ivbW +
    hbScore * weights.hbW + whiffScore * weights.whiffW;
  return Math.max(40, Math.min(200, Math.round(composite)));
}

/** Percentile (0-100) of v within a sorted ascending array. */
function percentileOf(sorted, v) {
  if (!sorted || sorted.length === 0) return null;
  let lo = 0, hi = sorted.length;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (sorted[mid] <= v) lo = mid + 1; else hi = mid;
  }
  return Math.round((lo / sorted.length) * 100);
}

const mean = (arr) => arr.length ? arr.reduce((s, v) => s + v, 0) / arr.length : 0;
const stddev = (arr, m) => {
  if (arr.length < 2) return 1;
  return Math.sqrt(arr.reduce((s, v) => s + (v - m) ** 2, 0) / (arr.length - 1)) || 1;
};

/**
 * kNN whiff prediction + nearest comps for a designed pitch.
 * Distance is standardized euclidean over velo / IVB / |HB| / spin.
 */
function predictWhiff(design, rows, stats) {
  if (!rows || rows.length < 5 || !stats) return { whiff: null, neighbors: [] };
  const scored = [];
  for (const r of rows) {
    if (r.whiff == null) continue;
    const dv = (design.velo - r.velo) / stats.stdVelo;
    const di = (design.ivb - (r.ivb ?? 0)) / stats.stdIvb;
    const dh = (Math.abs(design.hb) - Math.abs(r.hb ?? 0)) / stats.stdHb;
    const ds = stats.stdSpin > 1 && r.spin ? (design.spin - r.spin) / stats.stdSpin : 0;
    const dist = Math.sqrt(dv * dv + di * di + dh * dh + ds * ds);
    scored.push({ row: r, dist });
  }
  scored.sort((a, b) => a.dist - b.dist);
  const k = Math.min(15, scored.length);
  let num = 0, den = 0;
  for (let i = 0; i < k; i++) {
    const w = 1 / (0.25 + scored[i].dist);
    num += scored[i].row.whiff * w;
    den += w;
  }
  const whiff = den > 0 ? Math.max(3, Math.min(60, num / den)) : null;
  return { whiff, neighbors: scored };
}

// ============================================================================
// SMALL UI PIECES
// ============================================================================

const PercentileBar = ({ label, display, pct, color }) => (
  <Box sx={{ mb: 1 }}>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25 }}>
      <Typography variant="caption" fontWeight={600}>{label}</Typography>
      <Typography variant="caption" color="text.secondary">
        {display} · {pct != null ? `${pct}th %ile` : '—'}
      </Typography>
    </Box>
    <Box sx={{ height: 8, borderRadius: 4, bgcolor: (t) => alpha(t.palette.text.primary, 0.08), overflow: 'hidden' }}>
      <Box sx={{ height: '100%', width: `${pct ?? 0}%`, borderRadius: 4, bgcolor: color, transition: 'width 0.2s' }} />
    </Box>
  </Box>
);

/** SVG movement plot: |HB| (x) vs IVB (y). League dots + designed pitch. */
const MovementPlot = ({ rows, design, ptColor, compKeys }) => {
  const theme = useTheme();
  const W = 420, H = 320, PAD = 38;
  const xs = rows.map(r => Math.abs(r.hb ?? 0)).concat([Math.abs(design.hb)]);
  const ys = rows.map(r => r.ivb ?? 0).concat([design.ivb]);
  const xMin = Math.min(0, ...xs) - 2, xMax = Math.max(...xs, 4) + 2;
  const yMin = Math.min(...ys, -5) - 2, yMax = Math.max(...ys, 5) + 2;
  const px = (v) => PAD + ((v - xMin) / (xMax - xMin)) * (W - PAD * 2);
  const py = (v) => H - PAD - ((v - yMin) / (yMax - yMin)) * (H - PAD * 2);
  const gridColor = alpha(theme.palette.text.primary, 0.1);
  return (
    <Box sx={{ width: '100%', overflow: 'hidden' }}>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto', display: 'block' }}>
        {/* zero line for IVB */}
        {yMin < 0 && yMax > 0 && (
          <line x1={PAD} x2={W - PAD} y1={py(0)} y2={py(0)} stroke={gridColor} strokeDasharray="4 4" />
        )}
        {/* axes */}
        <line x1={PAD} x2={W - PAD} y1={H - PAD} y2={H - PAD} stroke={gridColor} />
        <line x1={PAD} x2={PAD} y1={PAD} y2={H - PAD} stroke={gridColor} />
        <text x={W / 2} y={H - 8} textAnchor="middle" fontSize="11" fill={theme.palette.text.secondary}>
          Horizontal Break (in)
        </text>
        <text x={12} y={H / 2} textAnchor="middle" fontSize="11" fill={theme.palette.text.secondary}
          transform={`rotate(-90 12 ${H / 2})`}>
          Induced Vertical Break (in)
        </text>
        {/* league dots */}
        {rows.map((r, i) => {
          const key = `${r.player}|${r.team}`;
          const isComp = compKeys?.has(key);
          return (
            <circle
              key={i}
              cx={px(Math.abs(r.hb ?? 0))} cy={py(r.ivb ?? 0)}
              r={isComp ? 5 : 3}
              fill={alpha(ptColor, isComp ? 0.85 : 0.25)}
              stroke={isComp ? theme.palette.text.primary : 'none'}
              strokeWidth={isComp ? 1 : 0}
            />
          );
        })}
        {/* designed pitch */}
        <circle cx={px(Math.abs(design.hb))} cy={py(design.ivb)} r={9}
          fill={ptColor} stroke={theme.palette.background.paper} strokeWidth={2.5} />
        <circle cx={px(Math.abs(design.hb))} cy={py(design.ivb)} r={13}
          fill="none" stroke={ptColor} strokeWidth={1.5} strokeDasharray="3 3" />
      </svg>
    </Box>
  );
};

// ============================================================================
// DESIGNER TAB
// ============================================================================

const DesignSlider = ({ label, unit, value, onChange, min, max, step, avg, color }) => (
  <Box sx={{ mb: 1.5 }}>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
      <Typography variant="body2" fontWeight={700}>{label}</Typography>
      <Typography variant="body2" fontWeight={800} sx={{ color }}>
        {value}{unit}
        <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.75 }}>
          lg avg {avg}{unit}
        </Typography>
      </Typography>
    </Box>
    <Slider
      size="small" value={value} min={min} max={max} step={step}
      onChange={(_, v) => onChange(v)}
      marks={avg != null ? [{ value: avg }] : []}
      sx={{ color, '& .MuiSlider-mark': { height: 10, width: 2, bgcolor: 'text.secondary' } }}
    />
  </Box>
);

const DesignerTab = ({ corpus, design, setDesign, onAddToArsenal, isMobile, navigate }) => {
  const theme = useTheme();
  const pt = design.pt;
  const ptColor = PITCH_COLORS[pt] || theme.palette.primary.main;
  const baseline = corpus?.baselines?.[pt] || null;
  const weights = corpus?.weights?.[pt] || null;

  // Per-type corpus rows + derived stats
  const rows = useMemo(
    () => (corpus?.pitches || []).filter(r => r.pt === pt),
    [corpus, pt]
  );
  const stats = useMemo(() => {
    if (rows.length === 0) return null;
    const velos = rows.map(r => r.velo).sort((a, b) => a - b);
    const ivbs = rows.map(r => r.ivb ?? 0).sort((a, b) => a - b);
    const hbs = rows.map(r => Math.abs(r.hb ?? 0)).sort((a, b) => a - b);
    const spins = rows.filter(r => r.spin).map(r => r.spin).sort((a, b) => a - b);
    const whiffs = rows.filter(r => r.whiff != null).map(r => r.whiff).sort((a, b) => a - b);
    const mVelo = mean(velos), mIvb = mean(ivbs), mHb = mean(hbs), mSpin = mean(spins);
    return {
      velos, ivbs, hbs, spins, whiffs,
      stdVelo: stddev(velos, mVelo), stdIvb: stddev(ivbs, mIvb),
      stdHb: stddev(hbs, mHb), stdSpin: stddev(spins, mSpin),
      avgSpin: Math.round(mSpin) || 2200,
    };
  }, [rows]);

  // Prediction + scoring (live, every slider move)
  const { whiff: predictedWhiff, neighbors } = useMemo(
    () => predictWhiff(design, rows, stats),
    [design, rows, stats]
  );
  const stuff = useMemo(
    () => (predictedWhiff != null ? stuffScore(design, baseline, weights, predictedWhiff) : null),
    [design, baseline, weights, predictedWhiff]
  );
  const grade = stuff != null ? gradeFor(stuff) : null;
  const comps = useMemo(() => neighbors.slice(0, 8), [neighbors]);
  const compKeys = useMemo(
    () => new Set(comps.slice(0, 5).map(c => `${c.row.player}|${c.row.team}`)),
    [comps]
  );

  const resetToAvg = useCallback(() => {
    if (!baseline) return;
    setDesign(d => ({
      ...d,
      velo: Math.round(baseline.velo * 10) / 10,
      ivb: Math.round(baseline.ivb * 10) / 10,
      hb: Math.round(baseline.hb * 10) / 10,
      spin: baseline.spin || stats?.avgSpin || 2200,
    }));
  }, [baseline, stats, setDesign]);

  // Slider bounds: baseline ± 4σ padded by observed extremes
  const bounds = useMemo(() => {
    if (!baseline) return null;
    const b = (center, std, lo, hi, obsLo, obsHi) => [
      Math.floor(Math.min(center - std * 4, obsLo ?? Infinity, lo)),
      Math.ceil(Math.max(center + std * 4, obsHi ?? -Infinity, hi)),
    ];
    return {
      velo: b(baseline.velo, baseline.stdVelo, baseline.velo - 8, baseline.velo + 8, stats?.velos?.[0], stats?.velos?.[stats.velos.length - 1]),
      ivb: b(baseline.ivb, baseline.stdIvb, -25, 25, stats?.ivbs?.[0], stats?.ivbs?.[stats.ivbs.length - 1]),
      hb: [0, Math.ceil(Math.max(baseline.hb + baseline.stdHb * 4, stats?.hbs?.[stats.hbs.length - 1] ?? 20, 20))],
      spin: [1200, 3400],
    };
  }, [baseline, stats]);

  if (!corpus) return null;
  if (!baseline || !weights) {
    return <Alert severity="info">No league baseline available for this pitch type/season yet.</Alert>;
  }

  return (
    <Grid container spacing={2}>
      {/* LEFT: controls */}
      <Grid item xs={12} md={5}>
        <Card sx={{ p: 2 }}>
          <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 1 }}>Pitch Type</Typography>
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 2 }}>
            {DESIGNER_TYPES.filter(c => corpus.baselines?.[c]).map(code => (
              <Chip
                key={code}
                label={PITCH_NAMES[code] || code}
                size="small"
                variant={pt === code ? 'filled' : 'outlined'}
                onClick={() => setDesign(d => ({ ...d, pt: code }))}
                sx={{
                  fontWeight: pt === code ? 700 : 500,
                  bgcolor: pt === code ? alpha(PITCH_COLORS[code] || '#666', 0.25) : undefined,
                  borderColor: PITCH_COLORS[code] || undefined,
                  fontSize: '0.72rem',
                }}
              />
            ))}
          </Box>

          <DesignSlider label="Velocity" unit=" mph" value={design.velo}
            onChange={(v) => setDesign(d => ({ ...d, velo: v }))}
            min={bounds.velo[0]} max={bounds.velo[1]} step={0.1}
            avg={baseline.velo} color={ptColor} />
          <DesignSlider label="Induced Vertical Break" unit='"' value={design.ivb}
            onChange={(v) => setDesign(d => ({ ...d, ivb: v }))}
            min={bounds.ivb[0]} max={bounds.ivb[1]} step={0.1}
            avg={baseline.ivb} color={ptColor} />
          <DesignSlider label="Horizontal Break" unit='"' value={design.hb}
            onChange={(v) => setDesign(d => ({ ...d, hb: v }))}
            min={bounds.hb[0]} max={bounds.hb[1]} step={0.1}
            avg={baseline.hb} color={ptColor} />
          <DesignSlider label="Spin Rate" unit=" rpm" value={design.spin}
            onChange={(v) => setDesign(d => ({ ...d, spin: v }))}
            min={bounds.spin[0]} max={bounds.spin[1]} step={10}
            avg={baseline.spin || stats?.avgSpin} color={ptColor} />

          <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
            <Button size="small" startIcon={<RestartAlt />} onClick={resetToAvg} variant="outlined">
              League Avg
            </Button>
            <Button size="small" startIcon={<LibraryAdd />} variant="contained" disabled={stuff == null}
              onClick={() => onAddToArsenal({ ...design, stuffPlus: stuff, whiff: predictedWhiff })}>
              Add to Arsenal
            </Button>
          </Stack>
        </Card>

        {/* Score card */}
        <Card sx={{ p: 2, mt: 2, border: `1px solid ${alpha(ptColor, 0.35)}` }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ textAlign: 'center', minWidth: 92 }}>
              <Typography variant="h3" fontWeight={900} sx={{ color: grade?.color, lineHeight: 1 }}>
                {stuff ?? '—'}
              </Typography>
              <Typography variant="caption" color="text.secondary" fontWeight={700}>STUFF+</Typography>
            </Box>
            <Divider orientation="vertical" flexItem />
            <Box sx={{ flex: 1 }}>
              {grade && (
                <Chip label={grade.label} size="small"
                  sx={{ bgcolor: alpha(grade.color, 0.18), color: grade.color, fontWeight: 800, mb: 0.75 }} />
              )}
              <Typography variant="body2">
                Predicted whiff rate:{' '}
                <b>{predictedWhiff != null ? `${predictedWhiff.toFixed(1)}%` : '—'}</b>
                <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                  (lg {baseline.whiff}%)
                </Typography>
              </Typography>
              <Typography variant="caption" color="text.secondary">
                vs {rows.length} real MLB {PITCH_NAMES[pt]?.toLowerCase()}s
              </Typography>
            </Box>
          </Box>
          <Box sx={{ mt: 1.5 }}>
            <PercentileBar label="Velocity" display={`${design.velo} mph`} color={ptColor}
              pct={percentileOf(stats?.velos, design.velo)} />
            <PercentileBar label="IVB" display={`${design.ivb}"`} color={ptColor}
              pct={weights.ivbDir === -1
                ? (100 - (percentileOf(stats?.ivbs, design.ivb) ?? 0))
                : percentileOf(stats?.ivbs, design.ivb)} />
            <PercentileBar label="Horizontal Break" display={`${design.hb}"`} color={ptColor}
              pct={percentileOf(stats?.hbs, Math.abs(design.hb))} />
            <PercentileBar label="Spin" display={`${design.spin} rpm`} color={ptColor}
              pct={percentileOf(stats?.spins, design.spin)} />
          </Box>
        </Card>
      </Grid>

      {/* RIGHT: movement plot + comps */}
      <Grid item xs={12} md={7}>
        <Card sx={{ p: 2 }}>
          <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 0.5 }}>
            Movement Map — your pitch vs the league
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Every dot is a real pitcher&apos;s average {PITCH_NAMES[pt]?.toLowerCase()} ({corpus.season}). Ringed dots are your closest comps.
          </Typography>
          <MovementPlot rows={rows} design={design} ptColor={ptColor} compKeys={compKeys} />
        </Card>

        <Card sx={{ mt: 2 }}>
          <Box sx={{ px: 2, py: 1.25, borderBottom: (t) => `1px solid ${alpha(t.palette.divider, 0.4)}` }}>
            <Typography variant="subtitle2" fontWeight={800}>
              Closest Real-World Comps
            </Typography>
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem' }}>Pitcher</TableCell>
                  {!isMobile && <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.72rem' }}>Team</TableCell>}
                  <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.72rem' }}>Velo</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.72rem' }}>IVB</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.72rem' }}>HB</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.72rem' }}>Whiff%</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.72rem' }}>Stuff+</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.72rem' }}>Match</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {comps.map((c, i) => {
                  const r = c.row;
                  const match = Math.max(1, Math.round(100 * Math.exp(-c.dist / 1.6)));
                  return (
                    <TableRow key={i} hover sx={{ cursor: 'pointer' }}
                      onClick={() => navigate(playerProfileUrl(r.player))}>
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem' }}>{r.player}</TableCell>
                      {!isMobile && <TableCell align="center" sx={{ fontSize: '0.78rem' }}>{r.team}</TableCell>}
                      <TableCell align="center" sx={{ fontSize: '0.78rem' }}>{r.velo?.toFixed(1)}</TableCell>
                      <TableCell align="center" sx={{ fontSize: '0.78rem' }}>{r.ivb?.toFixed(1)}"</TableCell>
                      <TableCell align="center" sx={{ fontSize: '0.78rem' }}>{Math.abs(r.hb ?? 0).toFixed(1)}"</TableCell>
                      <TableCell align="center" sx={{ fontSize: '0.78rem' }}>{r.whiff?.toFixed(1)}%</TableCell>
                      <TableCell align="center">
                        <Chip label={r.stuffPlus ?? '—'} size="small"
                          sx={{ fontWeight: 800, height: 20, fontSize: '0.72rem',
                            bgcolor: alpha(gradeFor(r.stuffPlus || 100).color, 0.15),
                            color: gradeFor(r.stuffPlus || 100).color }} />
                      </TableCell>
                      <TableCell align="center" sx={{ fontSize: '0.78rem', fontWeight: 700 }}>{match}%</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      </Grid>
    </Grid>
  );
};

// ============================================================================
// ARSENAL BUILDER TAB
// ============================================================================

const ArsenalTab = ({ corpus, arsenal, setArsenal, goToDesigner, navigate }) => {
  const theme = useTheme();

  // Unique pitchers in corpus for cloning
  const pitcherOptions = useMemo(() => {
    const map = new Map();
    for (const r of (corpus?.pitches || [])) {
      const key = `${r.player}|${r.team}`;
      if (!map.has(key)) map.set(key, { key, label: `${r.player} (${r.team})`, player: r.player, team: r.team });
    }
    return Array.from(map.values()).sort((a, b) => a.player.localeCompare(b.player));
  }, [corpus]);

  const cloneArsenal = useCallback((opt) => {
    if (!opt) return;
    const rows = (corpus?.pitches || [])
      .filter(r => r.player === opt.player && r.team === opt.team)
      .sort((a, b) => (b.usage || 0) - (a.usage || 0));
    setArsenal(rows.map((r, i) => ({
      id: `${Date.now()}-${i}`,
      pt: r.pt,
      velo: r.velo, ivb: r.ivb ?? 0, hb: Math.abs(r.hb ?? 0), spin: r.spin || 2200,
      usage: r.usage || 10,
      stuffPlus: r.stuffPlus, whiff: r.whiff,
      source: `${r.player} (${r.team})`,
    })));
  }, [corpus, setArsenal]);

  // Composite arsenal Stuff+ (usage-weighted)
  const composite = useMemo(() => {
    const valid = arsenal.filter(p => p.stuffPlus != null && p.usage > 0);
    const totalUsage = valid.reduce((s, p) => s + p.usage, 0);
    if (totalUsage === 0) return null;
    return Math.round(valid.reduce((s, p) => s + p.stuffPlus * p.usage, 0) / totalUsage);
  }, [arsenal]);
  const grade = composite != null ? gradeFor(composite) : null;

  // Real-pitcher comps: closest composite Stuff+ with overlapping repertoire
  const arsenalComps = useMemo(() => {
    if (composite == null || !corpus?.pitches) return [];
    const myTypes = new Set(arsenal.map(p => p.pt));
    const byPitcher = new Map();
    for (const r of corpus.pitches) {
      const key = `${r.player}|${r.team}`;
      if (!byPitcher.has(key)) byPitcher.set(key, { player: r.player, team: r.team, rows: [] });
      byPitcher.get(key).rows.push(r);
    }
    const scored = [];
    for (const p of byPitcher.values()) {
      const valid = p.rows.filter(r => r.stuffPlus != null && r.usage > 0);
      const tu = valid.reduce((s, r) => s + r.usage, 0);
      if (tu < 50) continue; // needs a real repertoire share
      const comp = valid.reduce((s, r) => s + r.stuffPlus * r.usage, 0) / tu;
      const theirTypes = new Set(p.rows.map(r => r.pt));
      const inter = [...myTypes].filter(t => theirTypes.has(t)).length;
      const union = new Set([...myTypes, ...theirTypes]).size;
      const jaccard = union > 0 ? inter / union : 0;
      const score = Math.abs(comp - composite) + (1 - jaccard) * 12;
      scored.push({ ...p, composite: Math.round(comp), jaccard, score, types: [...theirTypes] });
    }
    scored.sort((a, b) => a.score - b.score);
    return scored.slice(0, 6);
  }, [arsenal, composite, corpus]);

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={7}>
        <Card sx={{ p: 2 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}
            alignItems={{ xs: 'stretch', sm: 'center' }} justifyContent="space-between" sx={{ mb: 2 }}>
            <Typography variant="subtitle2" fontWeight={800}>Your Arsenal ({arsenal.length} pitches)</Typography>
            <Stack direction="row" spacing={1}>
              <Autocomplete
                size="small" options={pitcherOptions} sx={{ minWidth: 220 }}
                onChange={(_, v) => cloneArsenal(v)}
                renderInput={(params) => (
                  <TextField {...params} placeholder="Clone a real pitcher..."
                    InputProps={{ ...params.InputProps,
                      startAdornment: <PersonSearch fontSize="small" sx={{ color: 'text.secondary', mr: 0.5 }} /> }} />
                )}
              />
              <Button size="small" variant="outlined" startIcon={<AddCircleOutline />} onClick={goToDesigner}>
                Design
              </Button>
            </Stack>
          </Stack>

          {arsenal.length === 0 ? (
            <Alert severity="info" icon={<Science />}>
              Build an arsenal: design pitches in the Designer tab and add them here, or clone a real pitcher&apos;s
              full repertoire to start from.
            </Alert>
          ) : arsenal.map((p) => {
            const c = PITCH_COLORS[p.pt] || '#666';
            const g = gradeFor(p.stuffPlus || 100);
            return (
              <Box key={p.id} sx={{
                display: 'flex', alignItems: 'center', gap: 1.5, py: 1, px: 1.5, mb: 1,
                borderRadius: 2, border: `1px solid ${alpha(c, 0.3)}`, bgcolor: alpha(c, 0.04),
              }}>
                <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: c, flexShrink: 0 }} />
                <Box sx={{ minWidth: 120 }}>
                  <Typography variant="body2" fontWeight={800}>{PITCH_NAMES[p.pt] || p.pt}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {p.velo?.toFixed(1)} mph · {p.ivb?.toFixed(1)}" IVB · {p.hb?.toFixed(1)}" HB
                    {p.source ? ` · from ${p.source}` : ''}
                  </Typography>
                </Box>
                <Box sx={{ flex: 1, px: 1 }}>
                  <Typography variant="caption" color="text.secondary">Usage {p.usage?.toFixed(0)}%</Typography>
                  <Slider size="small" value={p.usage} min={2} max={80} step={1}
                    onChange={(_, v) => setArsenal(a => a.map(x => x.id === p.id ? { ...x, usage: v } : x))}
                    sx={{ color: c, py: 0.5 }} />
                </Box>
                <Chip label={`${p.stuffPlus ?? '—'}`} size="small"
                  sx={{ fontWeight: 800, bgcolor: alpha(g.color, 0.15), color: g.color }} />
                <IconButton size="small" onClick={() => setArsenal(a => a.filter(x => x.id !== p.id))}>
                  <Delete fontSize="small" />
                </IconButton>
              </Box>
            );
          })}
        </Card>
      </Grid>

      <Grid item xs={12} md={5}>
        <Card sx={{ p: 2, textAlign: 'center' }}>
          <Typography variant="caption" color="text.secondary" fontWeight={700}>ARSENAL STUFF+</Typography>
          <Typography variant="h2" fontWeight={900} sx={{ color: grade?.color || 'text.disabled', lineHeight: 1.1 }}>
            {composite ?? '—'}
          </Typography>
          {grade && (
            <Chip label={grade.label} size="small"
              sx={{ bgcolor: alpha(grade.color, 0.18), color: grade.color, fontWeight: 800, mt: 0.5 }} />
          )}
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
            Usage-weighted composite of your {arsenal.length} pitch{arsenal.length === 1 ? '' : 'es'}
          </Typography>
        </Card>

        {arsenalComps.length > 0 && (
          <Card sx={{ mt: 2 }}>
            <Box sx={{ px: 2, py: 1.25, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.4)}` }}>
              <Typography variant="subtitle2" fontWeight={800}>Pitchers You Resemble</Typography>
            </Box>
            {arsenalComps.map((p, i) => (
              <Box key={i} onClick={() => navigate(playerProfileUrl(p.player))}
                sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1,
                  cursor: 'pointer', '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.05) },
                  borderBottom: i < arsenalComps.length - 1 ? `1px solid ${alpha(theme.palette.divider, 0.25)}` : 'none' }}>
                <Box>
                  <Typography variant="body2" fontWeight={700}>{p.player} <Typography component="span" variant="caption" color="text.secondary">({p.team})</Typography></Typography>
                  <Box sx={{ display: 'flex', gap: 0.4, mt: 0.25 }}>
                    {p.types.slice(0, 6).map(t => (
                      <Box key={t} sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: PITCH_COLORS[t] || '#666' }} />
                    ))}
                  </Box>
                </Box>
                <Chip label={p.composite} size="small"
                  sx={{ fontWeight: 800, bgcolor: alpha(gradeFor(p.composite).color, 0.15), color: gradeFor(p.composite).color }} />
              </Box>
            ))}
          </Card>
        )}
      </Grid>
    </Grid>
  );
};

// ============================================================================
// PAGE
// ============================================================================

const PitchLab = () => {
  const theme = useTheme();
  const { year } = useYear();
  const { isMobile } = useMobile();
  const navigate = useNavigate();

  const [tab, setTab] = useState(0);
  const [corpus, setCorpus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [design, setDesign] = useState({ pt: 'FF', velo: 94, ivb: 16, hb: 7, spin: 2300 });
  const [arsenal, setArsenal] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await pitchSplitsApi.getLabCorpus(year, { minPitches: 30 });
        if (!cancelled) setCorpus(data);
      } catch (e) {
        if (!cancelled) setError('Unable to load pitch corpus');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [year]);

  // When corpus loads or pitch type changes, snap sliders to league average
  useEffect(() => {
    const b = corpus?.baselines?.[design.pt];
    if (!b) return;
    setDesign(d => ({
      ...d,
      velo: Math.round(b.velo * 10) / 10,
      ivb: Math.round(b.ivb * 10) / 10,
      hb: Math.round(b.hb * 10) / 10,
      spin: b.spin || 2200,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [corpus, design.pt]);

  const addToArsenal = useCallback((p) => {
    setArsenal(a => [...a, {
      id: `${Date.now()}`,
      pt: p.pt, velo: p.velo, ivb: p.ivb, hb: p.hb, spin: p.spin,
      usage: Math.max(5, Math.round(80 / (a.length + 2))),
      stuffPlus: p.stuffPlus, whiff: p.whiff, source: null,
    }]);
    setTab(1);
  }, []);

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 }, maxWidth: 1400, mx: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{
            width: 40, height: 40, borderRadius: 2,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
          }}>
            <Speed sx={{ fontSize: 24, color: '#fff' }} />
          </Box>
          <Box>
            <Typography variant="h5" fontWeight={800} sx={{ fontSize: { xs: '1.3rem', sm: '1.6rem' }, lineHeight: 1.2 }}>
              Pitch Lab
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Design pitches, build arsenals, score them against the real league
            </Typography>
          </Box>
        </Box>
        <PageYearSelector />
      </Box>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" allowScrollButtonsMobile
        sx={{ mb: 2, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.4)}`,
          '& .MuiTab-root': { textTransform: 'none', fontWeight: 700, minHeight: 44 } }}>
        <Tab icon={<Science fontSize="small" />} iconPosition="start" label="Pitch Designer" />
        <Tab icon={<LibraryAdd fontSize="small" />} iconPosition="start" label={`Arsenal Builder${arsenal.length ? ` (${arsenal.length})` : ''}`} />
        <Tab icon={<LeaderboardIcon fontSize="small" />} iconPosition="start" label="Stuff+ Leaderboard" />
      </Tabs>

      {error && tab !== 2 && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading && tab !== 2 && (
        <Grid container spacing={2}>
          <Grid item xs={12} md={5}><Skeleton variant="rounded" height={420} /></Grid>
          <Grid item xs={12} md={7}><Skeleton variant="rounded" height={420} /></Grid>
        </Grid>
      )}

      {!loading && tab === 0 && (
        <DesignerTab corpus={corpus} design={design} setDesign={setDesign}
          onAddToArsenal={addToArsenal} isMobile={isMobile} navigate={navigate} />
      )}
      {!loading && tab === 1 && (
        <ArsenalTab corpus={corpus} arsenal={arsenal} setArsenal={setArsenal}
          goToDesigner={() => setTab(0)} navigate={navigate} />
      )}
      {tab === 2 && <PitchExplorer year={year} isMobile={isMobile} navigate={navigate} />}
    </Box>
  );
};

export default PitchLab;
